/**
 * Who may read cash flow (docs/39 §8.5, steward-app#2581, the GRANT release of slice 2).
 *
 * ## The capabilities
 *
 * | capability                    | what it reads                                    | held by |
 * | ----------------------------- | ------------------------------------------------ | ------- |
 * | {@link CASH_FLOW_READ_CAPABILITY}         | `cash-flow-month` (totals)                         | both family viewer roles, the firm totals role |
 * | {@link CASH_FLOW_TRANSACTIONS_CAPABILITY} | `transaction`, `transfer-pair` (every transaction) | the family detail role, the firm detail role |
 * | {@link CASH_FLOW_AGREEMENT_CAPABILITY}    | `cash-flow-agreement` (armed in release B, steward-app#2591) | `steward-admin` and all four cash flow roles |
 * | {@link CASH_FLOW_CURSOR_READ_CAPABILITY}  | `transaction-cursor`                              | NO pack role (system plane only) |
 * | {@link CASH_FLOW_CURSOR_WRITE_CAPABILITY} | `transaction-cursor` writes                       | NO pack role (system plane only) |
 *
 * "Held by" means held by a role this repo declares. appcore's `*:*` holder (platform admin)
 * implicitly satisfies every capability check, these included; that is the same aside
 * `packRoles.ts` and `scripts/lib/packArming.ts` make for every other capability.
 *
 * ⚠️ **No base family role holds either of the first two** (`steward-admin`, `steward-member`,
 * `steward-viewer`, and appcore's platform roles, which this repo never grants a pack
 * capability). That absence is what makes the "Nothing" viewing level possible (docs/39 §7
 * decision 3, §8.5): a family member sees cash flow only through one of the two composable
 * viewer roles below, which a family admin sets per person. `cashFlowAccess.test.ts` pins it.
 *
 * `steward-admin` holds the third, {@link CASH_FLOW_AGREEMENT_CAPABILITY}, and no other cash
 * flow string. It reads the on/off record, never a month or a transaction, so "Nothing" still
 * means no totals and no transactions for an admin too. See that constant for why.
 *
 * ## Why the firm roles are NOT sharing pillars
 *
 * docs/39 §8.5 first called the two firm roles "pillar roles". They are standalone composable
 * roles instead (coordinator decision on steward-app#2581, 2026-10-26), because a
 * `SHARING_PILLARS` entry means "an area every family base role holds": `packRoles.ts`
 * spreads `PILLAR_CAPABILITIES` into all three base roles and `pillars.test.ts`
 * (`role %s holds EVERY pillar capability`) pins that, so a cash flow pillar would hand both
 * capabilities to every family member and remove "Nothing". The pillar table is also read
 * TOTAL by the sponsored floor (`FULL_SPONSORED_FLOOR_AREAS`) and LIVE by sponsored setup and
 * What you share. How cash flow appears in a firm's shared areas, the sponsored floor and the
 * disclosure copy were slice 10's decision (steward-app#2589): see {@link FIRM_CASH_FLOW_MODES}.
 *
 * Being outside the pillar table means nothing derives these ids into the advisor-scope
 * lists for free, so `roles.ts` lists the firm ids in `ADVISOR_SCOPE_ROLE_IDS` and all four
 * in `NO_WRITE_ROLE_IDS` by hand, and the tests pin each membership.
 *
 * ## Grant before arm
 *
 * Slice 2 (steward-app#2581) granted the capabilities to the roles below and declared the
 * types with no `readCapability`. Slice 4 (steward-app#2583), a later release, arms each type
 * with the gate in the table above (`cashFlowTypeDefs.ts`). See `pillars.ts`'s module docblock
 * for why the grant and the arm must be separate deploys. `cashFlowAccess.test.ts` pins each
 * armed capability as held by a pack role (or system-only), so a role edit that strands a
 * gated type reds.
 *
 * ## Frozen once seeded
 *
 * Every string and id here is frozen once seeded, for the reason `SharingPillar.capability`
 * and `SharingPillar.roleId` give: a rename leaves the old string on persisted role rows and
 * the new one granted nowhere, and appcore's role cache is keyed by roleId alone.
 */

/** Totals: the monthly aggregates. (The agreement moved to {@link CASH_FLOW_AGREEMENT_CAPABILITY} in release B.) */
export const CASH_FLOW_READ_CAPABILITY = 'cash-flow:read';

/** Every transaction and every transfer pair. */
export const CASH_FLOW_TRANSACTIONS_CAPABILITY = 'cash-flow:transactions';

/**
 * The agreement: whether cash flow is on, its window and the included accounts
 * (steward-app#2685, docs/39 §8.5). Granted in release A (steward-app#2685); release B (slice 12,
 * steward-app#2591) arms `cash-flow-agreement` with it as the read gate and `mem:manage` as the
 * write gate (`cashFlowTypeDefs.ts`).
 *
 * Its own string because no single existing capability reaches the right population:
 *
 * - `steward-admin` must read the agreement whatever its own viewing level, including
 *   "Nothing", to turn cash flow on, set levels or turn it off. Before acceptance nobody holds
 *   a viewer role at all, so with the agreement gated on {@link CASH_FLOW_READ_CAPABILITY} no
 *   admin could create it. Granting `cash-flow:read` to `steward-admin` instead would hand
 *   every admin the monthly totals and remove "Nothing" for admins.
 * - Every cash flow role holds it, family and firm, because each of their reads checks the
 *   agreement first on the reader's own token (a firm viewer's summary read included).
 *
 * Held by no other PACK role (either pack). appcore's `*:*` holder (platform admin) reaches it
 * as it reaches every capability. appcore's `workspace-admin` (a platform role this repo never
 * grants a pack capability) does not hold it; founders reach it through `steward-admin`, which
 * appcore onboarding puts on every founder membership beside `workspace-admin` (appcore
 * `packages/core/src/db/onboarding-repository.ts`, `packAdminRoleIds`). No advisor-scope role
 * gains `mem:manage` or any write from it: it is a read string.
 *
 * Now that release B arms it, a membership holding ONLY `workspace-admin` (no `steward-admin`)
 * passes the agreement's write gate (`mem:manage`) but not its read gate, and appcore requires
 * the read capability for a write too, so it can neither read nor write the agreement. That
 * fails closed; such a membership needs `steward-admin` added, not a wider grant here.
 */
export const CASH_FLOW_AGREEMENT_CAPABILITY = 'cash-flow:agreement';

/**
 * `transaction-cursor`'s read gate. Held by no role: account-sync reads cursors over
 * `/system/*`, which runs no capability gate. Its own resource segment, so no
 * `cash-flow:*` grant would reach it.
 */
export const CASH_FLOW_CURSOR_READ_CAPABILITY = 'cash-flow-cursor:read';

/** `transaction-cursor`'s write gate. Held by no role, for the same reason. */
export const CASH_FLOW_CURSOR_WRITE_CAPABILITY = 'cash-flow-cursor:write';

/**
 * The four cash flow roles. Fresh ids minted 2026-10-26 for steward-app#2581, in the
 * 24-character `[0-9A-Z]` shape every declared pack role id has (`packRoles.test.ts`).
 *
 * Each also holds {@link CASH_FLOW_AGREEMENT_CAPABILITY} (steward-app#2685).
 *
 * - `FAMILY_TOTALS` (`steward-cash-flow-totals`): {@link CASH_FLOW_READ_CAPABILITY}.
 * - `FAMILY_DETAIL` (`steward-cash-flow-detail`): both read capabilities, so detail implies
 *   totals with one role.
 * - `FIRM_TOTALS` (`steward-share-cash-flow`): {@link CASH_FLOW_READ_CAPABILITY}.
 * - `FIRM_DETAIL` (`steward-share-cash-flow-detail`): {@link CASH_FLOW_TRANSACTIONS_CAPABILITY}
 *   and not the totals one. docs/39 §8.5 has the façade compose `FIRM_TOTALS` beside it (slice
 *   10), rather than folding totals into this role.
 *
 * ⚠️ The family ids are family roles and must never be on an advisor membership (slice 11's
 * guard); they are deliberately NOT in `ADVISOR_SCOPE_ROLE_IDS`, which also decides who IS an
 * advisor (`advisorUserIds`, `canBeAdvisor`, the invite-accept refusal), so listing them would
 * class every family member holding one as an advisor. The façade's `familyRoleIds` strips
 * them from folder ACL harvesting separately. The firm ids are advisor scope and ARE in it.
 *
 * The firm pair is composed by `PUT /advisor/shared-areas/{firmId}` through {@link firmCashFlowRoleIds}
 * (slice 10); the family pair by the viewer and join paths (slices 11, 12).
 */
export const CASH_FLOW_ROLE_IDS = {
  FAMILY_TOTALS: '01HZSTEWRDCFTOT000000001',
  FAMILY_DETAIL: '01HZSTEWRDCFDTL000000001',
  FIRM_TOTALS:   '01HZSTEWRDCFSHT000000001',
  FIRM_DETAIL:   '01HZSTEWRDCFSHD000000001',
} as const;

/** The two composable FAMILY viewer roles (docs/39 §8.5 levels "Totals only" and "Every
 *  transaction"). */
export const CASH_FLOW_FAMILY_VIEWER_ROLE_IDS: readonly string[] = [
  CASH_FLOW_ROLE_IDS.FAMILY_TOTALS,
  CASH_FLOW_ROLE_IDS.FAMILY_DETAIL,
];

/** The two composable FIRM roles. Advisor scope. */
export const CASH_FLOW_FIRM_ROLE_IDS: readonly string[] = [
  CASH_FLOW_ROLE_IDS.FIRM_TOTALS,
  CASH_FLOW_ROLE_IDS.FIRM_DETAIL,
];

/**
 * What one advisory firm sees of a family's cash flow (docs/39 §7 decision 3, §8.5 "Firm
 * modes", slice 10, steward-app#2589). One field on `GET`/`PUT /advisor/shared-areas`, never two
 * area booleans, so the wire cannot say "every transaction without totals".
 *
 * - `nothing`: neither firm role. The default, for every connection and every sponsorship: a
 *   sponsored floor never includes cash flow (Matt, 2026-11-02), because neither firm role is a
 *   pillar role and every floor, default and sponsored-start set is derived from the pillar table.
 *   `cashFlowAccess.test.ts` pins that against each of those derivations.
 * - `totals`: {@link CASH_FLOW_ROLE_IDS.FIRM_TOTALS} only.
 * - `detail`: BOTH firm roles. Detail implies totals: the firm detail role holds only
 *   {@link CASH_FLOW_TRANSACTIONS_CAPABILITY}, so {@link firmCashFlowRoleIds} composes the totals
 *   role beside it rather than the detail role folding totals in.
 */
export const FIRM_CASH_FLOW_MODES = ['nothing', 'totals', 'detail'] as const;
export type FirmCashFlowMode = typeof FIRM_CASH_FLOW_MODES[number];

/**
 * The firm role ids one mode composes, in a fixed order (totals first). The ONE place a mode
 * becomes role ids; the façade's `setSharedAreas` writes exactly this for a registry advisor.
 * `detail` always yields both ids, never the detail role alone (pinned in both directions by
 * `cashFlowAccess.test.ts`). A fresh array on every call.
 */
export function firmCashFlowRoleIds(mode: FirmCashFlowMode): string[] {
  switch (mode) {
    case 'nothing': return [];
    case 'totals': return [CASH_FLOW_ROLE_IDS.FIRM_TOTALS];
    case 'detail': return [CASH_FLOW_ROLE_IDS.FIRM_TOTALS, CASH_FLOW_ROLE_IDS.FIRM_DETAIL];
  }
}

/**
 * The mode a role set (or the union of a firm's advisors' role sets) carries: the highest firm
 * role held. A set holding the detail role reads `detail` even without the totals role (which
 * nothing in the façade writes), because that is the higher of what its capabilities allow, the
 * same "highest role held" rule the family levels use (docs/39 §8.5). It REPORTS; it decides no
 * write.
 */
export function firmCashFlowModeOf(roleIds: Iterable<string>): FirmCashFlowMode {
  const held = new Set(roleIds);
  if (held.has(CASH_FLOW_ROLE_IDS.FIRM_DETAIL)) return 'detail';
  if (held.has(CASH_FLOW_ROLE_IDS.FIRM_TOTALS)) return 'totals';
  return 'nothing';
}

/** Every cash flow type code (docs/39 §8.2). */
export const CASH_FLOW_TYPE_CODES = [
  'transaction', 'transfer-pair', 'cash-flow-month', 'cash-flow-agreement', 'transaction-cursor',
] as const;

/**
 * The account types whose transactions cash flow stores (docs/39 §7 decision 2: checking, savings,
 * cash management, money market and card accounts; Matt, 2026-11-02). The set holds lower-case
 * values. Compared to the stored `account.accountType`, which account-sync writes as the
 * lower-cased Plaid subtype, or the type when there is no subtype (`mapAccountType`); both
 * account-sync (which lower-cases the stored value, `transactionsSync.ts`) and the Setup page
 * lower-case it before the `.has`, so the two agree. `credit` is the type fallback for a card
 * with no subtype. Anything else (`cd`, `depository` with no subtype, investment and loan types)
 * is skipped.
 *
 * One set for both sides: account-sync's transactions loop skips every other type, and the Setup
 * page lists only these, so what it says Steward stores is what Steward stores (steward-app#2592).
 * For reference, a scan of appcore-dev run by the author on 2026-11-02 (58 plaid-sourced `account`
 * rows, one scan, no pagination left) held checking, savings, cash management, money market and
 * credit card in exactly this lower-case form alongside 401k, ira, hsa, cd, mortgage and others;
 * it is a point-in-time reading, not an invariant.
 */
export const CASH_FLOW_ACCOUNT_TYPES: ReadonlySet<string> = new Set(['checking', 'savings', 'cash management', 'money market', 'credit card', 'credit']);
